import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { updateVideoProgress } from '../../../api_service';

// Detect provider and build embed URL
const parseVideoUrl = (url) => {
  if (!url) return { type: 'file', src: '' };
  const u = url.trim();

  // YouTube
  let ytId = null;
  const watchMatch = u.match(/youtube\.com\/watch\?v=([^&]+)/);
  const shortMatch = u.match(/youtu\.be\/([^?&/]+)/);
  if (watchMatch) ytId = watchMatch[1];
  else if (shortMatch) ytId = shortMatch[1];
  if (ytId) return { type: 'youtube', src: `https://www.youtube.com/embed/${ytId}` };

  // Vimeo
  const vimeoMatch = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return { type: 'vimeo', src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };

  // Fallback: HTML5 video
  return { type: 'file', src: u };
};

const VideoPlayerModal = ({ item, traineeId, onClose, onUpdated }) => {
  const video = item.video;
  const { type, src } = parseVideoUrl(video?.videoUrl);
  const videoRef = useRef(null);
  const lastSavedRef = useRef(0);

  const [watchedSeconds, setWatchedSeconds] = useState(item.progress?.watchedSeconds || 0);
  const [completed, setCompleted] = useState(item.progress?.completed || false);
  const [saving, setSaving] = useState(false);

  const duration = video?.durationSeconds;
  const pct = completed
    ? 100
    : duration && duration > 0
      ? Math.min(100, Math.round((watchedSeconds / duration) * 100))
      : 0;

  const save = async (secs, done) => {
    try {
      setSaving(true);
      await updateVideoProgress(video.videoId, traineeId, {
        watchedSeconds: Math.floor(secs),
        completed: done,
      });
      if (onUpdated) onUpdated();
    } catch {
      // ignore transient save errors
    } finally {
      setSaving(false);
    }
  };

  // HTML5 video time tracking
  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    const cur = el.currentTime;
    setWatchedSeconds(cur);
    // Save roughly every 10s of watch time
    if (cur - lastSavedRef.current >= 10) {
      lastSavedRef.current = cur;
      save(cur, completed);
    }
  };

  const handlePause = () => {
    const el = videoRef.current;
    if (el) save(el.currentTime, completed);
  };

  const handleEnded = () => {
    setCompleted(true);
    const el = videoRef.current;
    save(el ? el.currentTime : (duration || 0), true);
  };

  // Persist progress for HTML5 video when modal unmounts
  useEffect(() => {
    return () => {
      const el = videoRef.current;
      if (type === 'file' && el && el.currentTime > 0 && !completed) {
        save(el.currentTime, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkWatched = async () => {
    // Require the video to be (almost) fully watched before it can be marked complete.
    if (type === 'file' && pct < 95) return;
    setCompleted(true);
    await save(duration || watchedSeconds || 0, true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground truncate">{video?.title}</h2>
            {video?.description && (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{video.description}</p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none ml-4">&times;</button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {/* Player */}
          <div className="bg-black rounded-lg overflow-hidden aspect-video">
            {completed ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-center text-white p-6">
                <Icon name="CheckCircle" size={40} />
                <p className="mt-3 font-medium">You have already completed this video.</p>
                <p className="text-xs opacity-80 mt-1">Re-watching is disabled once a video is marked complete.</p>
              </div>
            ) : type === 'file' ? (
              <video
                ref={videoRef}
                src={src}
                controls
                className="w-full h-full"
                onTimeUpdate={handleTimeUpdate}
                onPause={handlePause}
                onEnded={handleEnded}
              />
            ) : (
              <iframe
                title={video?.title || 'video'}
                src={src}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-foreground">Progress</span>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${completed ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                {completed ? 'Completed' : 'In Progress'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${completed ? 'bg-success' : 'bg-primary'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
            </div>
            {type !== 'file' && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Icon name="Info" size={12} />
                Watch the video above, then mark it as watched when done.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!completed && (
            <Button onClick={handleMarkWatched} disabled={saving || (type === 'file' && pct < 95)} iconName="CheckCircle" iconSize={16}>
              {saving ? 'Saving...' : (type === 'file' && pct < 95 ? `Watch ${pct}% \u2013 finish to mark` : 'Mark as Watched')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerModal;
